/* eslint-disable @typescript-eslint/naming-convention */
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import type { EntityId } from '@reduxjs/toolkit';
import { useBoolean } from 'ahooks';
import { Col, Row, Space } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { RoleType, UserType } from 'api/models';
import { UserMeta } from 'api/user';
import { ReactComponent as Astronaut } from 'assets/svgs/astro.svg';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { Loading as LoadingAtom } from 'components/atoms/loading';
import { Modal } from 'components/atoms/modal';
import { Tag } from 'components/atoms/tag';
import { TimedRender } from 'components/atoms/utils/timed-render';
import { Button } from 'components/facade/button';
import { Select } from 'components/facade/select';
import {
  BodyLong01,
  ProductiveHeading05,
  ProductiveHeading06,
} from 'components/styles';
import {
  useTranslation,
  useTranslate,
  TranslationKey,
} from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  useUpdateCurrentUserMutation,
  useUpdateUserMetaMutation,
} from 'queries';
import { useUpdateHubspotMutation } from 'queries/hubspot';
import { useCreateRoleMutation, useVendorRolesQuery } from 'queries/roles';
import { logger } from 'services';
import { byLabel } from 'utils/comparators/by-label';
import { IPINFO_URL, RH_EVENT_ORIGIN } from './constants';

export interface IntroModalWindow extends Window {
  RevenueHero: any;
  rhWidget: any;
}

export const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0 !important;
  }
`;

export const modalMaskCSS = {
  zIndex: 1011,
};

const Title = styled.div`
  ${ProductiveHeading05};
`;

const Description = styled.div`
  ${BodyLong01};
`;

const WelcomeNote = styled(Col)`
  background-color: var(--gray-10);
`;

const PaddedContainer = styled.div`
  padding: var(--spacing-08) var(--spacing-06);
`;

const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;
`;

export const LoadingSpinner = styled(LoadingAtom)`
  padding: var(--spacing-03);
`;

const SvgContainer = styled.div`
  display: flex;
`;

const FormContainer = styled.div`
  width: var(--pane-width);
  padding: var(--spacing-07) var(--spacing-07) 0 var(--spacing-07);
`;

const Heading = styled.h6`
  ${ProductiveHeading06};
  margin-bottom: var(--layout-02);
`;

const Footer = styled.div`
  padding: var(--spacing-05);
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
`;

const ModalHeightCSS = css`
  height: 380px;
`;

const VideoContainer = styled(Col)`
  ${ModalHeightCSS};
`;

const Iframe = styled.iframe`
  ${ModalHeightCSS};
  width: 100%;
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: -40px;
`;

const ModalCloseButton = styled(Tag)<{ disabled: boolean }>`
  background-color: var(--gray-80);
  color: var(--white-100);
  border: 2px solid transparent;
  cursor: pointer;
  ${({ disabled }) =>
    disabled
      ? css`
          pointer-events: none;
        `
      : ''};
`;

export const RHTitle = styled.div`
  padding: var(--spacing-05);
  padding-bottom: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  ${ProductiveHeading06};

  span {
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export const RHContainer = styled.div<{ height: number }>`
  height: ${({ height }) => height}px;
  width: 672px;

  div {
    position: unset !important;
  }
`;

export const CloseButton = styled(Button)`
  position: absolute;
  top: var(--spacing-03);
  right: var(--spacing-03);
  z-index: var(--zindex-overlay-content);
`;

type Opt = { label: string; value: string };

const GOAL_OPTIONS = [
  {
    key: 'routes.vendor.accelerate_timetovalue',
    value: 'Accelerate time-to-value',
  },
  {
    key: 'routes.vendor.enhance_customer_visibility',
    value: 'Enhance customer visibility',
  },
  {
    key: 'routes.vendor.seamless_timetracking',
    value: 'Seamless time-tracking',
  },
  {
    key: 'routes.vendor.improve_utilization_rates',
    value: 'Improve utilization rates',
  },
  {
    key: 'routes.vendor.create_custom_reports',
    value: 'Create custom reports',
  },
] as const;

const INDUSTRY_OPTIONS = [
  {
    key: 'routes.vendor.saas_onboarding_and_implementation',
    value: 'SaaS - Onboarding and Implementation',
  },
  {
    key: 'routes.vendor.saas_professional_services',
    value: 'SaaS - Professional Services',
  },
  {
    key: 'routes.vendor.services_and_consultancy',
    value: 'Services and Consultancy',
  },
  { key: 'routes.vendor.agency', value: 'Agency' },
  { key: 'routes.vendor.others', value: 'Others' },
] as const;

const TEAM_OPTIONS = ['1 to 10', '11 to 50', '51 to 200', '200+'] as const;

/**
 * Key - User facing labels + Hubspot value
 * Value - Role name in Rocketlane
 */
const ROLES_MAP = {
  'Executives (C-suite, VP, Director)': 'Executive',
  'Implementation & delivery': 'Implementation Manager',
  'Onboarding Management': 'Onboarding Manager',
  'Project Management': 'Project Manager',
  'Resource Management': 'Resource Manager',
  Engineering: 'Engineer',
  Others: 'Others',
} as const;

const ROLES_TRANSLATION_KEYS_MAP = {
  'Executives (C-suite, VP, Director)': 'routes.vendor.roles.executive',
  'Implementation & delivery': 'routes.vendor.roles.implementation_manager',
  'Onboarding Management': 'routes.vendor.roles.onboarding_management',
  'Project Management': 'routes.vendor.roles.project_management',
  'Resource Management': 'routes.vendor.roles.resource_management',
  Engineering: 'routes.vendor.roles.engineer',
  Others: 'routes.vendor.roles.others',
} as const;

export enum IntroModalWidth {
  RevenueHeroWidget = 672,
  FormModal = 812,
}

export enum RevenueHeroState {
  Loading,
  Disqualified,
  Qualified,
}

const RH_TARGET = 'revenuehero_target';

type Map<T> = Record<EntityId, string | T> | Record<EntityId, number | T>;

const convertFormValuesToHubspotPayload = (
  values: Map<string | number | Opt>,
): Record<EntityId, string | number> => {
  return Object.entries(values).reduce((obj, entry) => {
    const [key, value] = entry;

    if (Array.isArray(value)) {
      // For checkboxes, Hubspot expects a semicolon separated string
      obj[key] = Object.values(
        convertFormValuesToHubspotPayload(value as any),
      ).join(';');
    } else if (typeof value === 'object') {
      obj[key] = value.label;
    } else {
      obj[key] = value;
    }

    return obj;
  }, {});
};

export const injectRHWidget = ({
  emailId,
  rhState,
  setRhState,
  setRHData,
  onFinish,
}) => {
  if (!emailId) {
    return;
  }

  const _window = window as unknown as IntroModalWindow;
  const script = document.createElement('script');

  script.onload = async () => {
    _window.rhWidget = new _window.RevenueHero({
      routerId: '1440',
      greetingText: '',
    });

    let country = '';

    try {
      const ipResponse = await fetch(IPINFO_URL);
      const ipData = await ipResponse.json();

      country = ipData.country;
    } catch (e) {
      logger.error('Unable to fetch IP address', { error: e });
    }

    _window.rhWidget
      .submit({
        email: emailId,
        ip_detected_country: country,
      })
      .then((data) => {
        if (data) {
          setRHData(data);
          setRhState(RevenueHeroState.Qualified);
        } else if (rhState === RevenueHeroState.Loading) {
          // User was disqualified from the meeting
          setRhState(RevenueHeroState.Disqualified);
        } else {
          // User was disqualified and the widget is being shown currently, so close it
          onFinish();
        }
      });
  };
  script.src = 'https://app.revenuehero.io/scheduler.min.js';
  document.body.appendChild(script);
};

const RHWidget = ({ close, form, rhData }) => {
  const { rhWidget: hero } = window as unknown as IntroModalWindow;
  const loading = !hero || !rhData;
  const [rhHeight, setRHHeight] = useState(489);
  const [isBooking, setIsBooking] = useState(true);

  useEffect(() => {
    if (loading) {
      return;
    }

    const messageEventCallback = (event: MessageEvent) => {
      if (event.origin !== RH_EVENT_ORIGIN || !event.isTrusted) {
        return;
      }

      if (event.data?.type === 'RESIZE_IFRAME') {
        setRHHeight(event.data.height + 4);
      } else if (event.data?.type === 'MEETING_BOOKED') {
        setIsBooking(false);
      }
    };

    try {
      window.addEventListener('message', messageEventCallback);
      hero.dialog.setEmbedTarget(`#${RH_TARGET}`);
      hero.dialog.open(rhData);
    } catch (e) {
      logger.error('Unable to show Revenue Hero widget', { error: e });
      close();
    }

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('message', messageEventCallback);
    };
  }, [form, close, rhData, loading, hero?.dialog]);

  if (loading) {
    return (
      <>
        <LoadingSpinner size="large" />
      </>
    );
  }

  return (
    <>
      <RHTitle>
        <span>
          {isBooking && <FormattedMessage id="signup_onboarding_rh" />}
        </span>
        <CloseButton type="text" onClick={close}>
          <FormattedMessage id={isBooking ? 'skip' : 'close'} />
        </CloseButton>
      </RHTitle>
      <RHContainer id={RH_TARGET} height={rhHeight} />
    </>
  );
};

const IntroVideo = ({ firstName, onClose }) => {
  const { userMeta } = useUser();
  const metaData = userMeta?.metaData;
  const { mutate, isLoading } = useUpdateUserMetaMutation();
  const handleClose = () => {
    mutate({ metaData: { ...metaData, profileSetupDone: true } });
    onClose();
  };

  const translate = useTranslate();

  return (
    <>
      <CloseButtonContainer>
        <ModalCloseButton onClick={handleClose} disabled={isLoading}>
          <FormattedMessage id="close" />
        </ModalCloseButton>
      </CloseButtonContainer>
      <Row gutter={0}>
        <WelcomeNote span={8}>
          <PaddedContainer>
            <Space direction="vertical" size={32}>
              <Title>
                <FormattedMessage
                  id="intro_modal.title"
                  values={{
                    firstName,
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Title>
              <Description>
                <Space direction="vertical" size={24}>
                  <FormattedMessage id="intro_modal.message.1" />
                  <FormattedMessage id="intro_modal.message.2" />
                </Space>
              </Description>
            </Space>
          </PaddedContainer>
        </WelcomeNote>
        <VideoContainer span={16}>
          <Iframe
            src="https://www.youtube.com/embed/Md0b2WaAUj4?si=m80ieirq8TTSghSJ"
            title={translate('routes.vendor.rocketlane_overview')}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </VideoContainer>
      </Row>
    </>
  );
};

const IntroModalContent = ({
  firstName,
  emailId,
  userType,
  originalRole,
  onFinish,
  rhState,
  setRhState,
}) => {
  const { data: roles, isLoading: isRolesLoading } = useVendorRolesQuery();
  const { mutateAsync: updateMeta, isLoading: isUpdatingMeta } =
    useUpdateUserMetaMutation();
  const { mutateAsync: updateHubspot, isLoading: isUpdatingHubspot } =
    useUpdateHubspotMutation();
  const { mutateAsync: updateCurrentUser, isLoading: isUpdatingUser } =
    useUpdateCurrentUserMutation();
  const { mutateAsync: createRole, isLoading: isCreatingRole } =
    useCreateRoleMutation();

  const [form] = Form.useForm();

  const roleValidationText = useTranslation(
    'intro_modal.form.role.validation.required',
  );
  const teamValidationText = useTranslation(
    'intro_modal.form.team.validation.required',
  );
  const industryValidationText = useTranslation(
    'intro_modal.form.industry.validation.required',
  );
  const goalsValidationText = useTranslation(
    'intro_modal.form.goals.validation.required',
  );

  const translate = useTranslate();

  const handleSubmit = async (values) => {
    const { role } = values;
    let newlyCreatedRole;

    if (role.__isNew__) {
      newlyCreatedRole = await createRole({
        roleName: role.value,
        use_as_placeholder: false,
        type: RoleType.Vendor,
      });
    }
    await updateCurrentUser(
      {
        role: {
          roleId: newlyCreatedRole ? newlyCreatedRole.roleId : role.value,
          roleName: newlyCreatedRole
            ? newlyCreatedRole.roleName
            : ROLES_MAP[role.label],
          type: newlyCreatedRole ? newlyCreatedRole.type : RoleType.Vendor,
        },
      },
      {
        onSuccess: async () => {
          await updateHubspot(
            { user: convertFormValuesToHubspotPayload(values) },
            {
              onSuccess: async () => {
                await updateMeta(
                  { metaData: { profileSetupDone: true } },
                  {
                    onSuccess: () => {
                      if (rhState === RevenueHeroState.Disqualified) {
                        // User did not qualify for a meeting
                        onFinish();
                      } else if (userType !== UserType.Partner) {
                        setRhState(RevenueHeroState.Loading);
                      } else {
                        onFinish();
                      }
                    },
                  },
                );
              },
            },
          );
        },
      },
    );
  };

  const rolesOptions = useMemo(() => {
    const isPartner = userType === UserType.Partner;
    const availableRoles = isPartner
      ? (roles ?? []).filter(({ usedByPartners }) => usedByPartners === true)
      : roles ?? [];

    if (isPartner) {
      return availableRoles
        .map(({ roleName, roleId }) => ({ label: roleName, value: roleId }))
        .sort(byLabel);
    }

    return Object.entries(ROLES_MAP).map(([label, roleName]) => {
      const existingRole = availableRoles.find(
        (role) => role.roleName === roleName,
      );

      return {
        label: translate(ROLES_TRANSLATION_KEYS_MAP[label] as TranslationKey),
        value: existingRole?.roleId ?? roleName,

        __isNew__: existingRole === undefined,
      };
    });
  }, [roles, userType, translate]);

  const isLoading =
    isCreatingRole || isUpdatingHubspot || isUpdatingMeta || isUpdatingUser;

  const showAccountCreateOnboardingModal = isEmpty(originalRole) || isLoading;
  const [rhData, setRHData] = useState(null);

  useEffect(() => {
    if (
      rhState === RevenueHeroState.Loading &&
      userType !== UserType.Partner &&
      (window as unknown as IntroModalWindow).rhWidget === undefined
    ) {
      // Run only once
      injectRHWidget({
        emailId,
        rhState,
        setRhState,
        setRHData,
        onFinish,
      });
    }
  }, [
    emailId,
    rhState,
    setRhState,
    setRHData,
    onFinish,
    userType,
    originalRole,
  ]);

  if (rhState === RevenueHeroState.Qualified) {
    return (
      <TimedRender
        hideAfterMs={2_500}
        retainIf={() => rhData !== null}
        onHide={() => {
          logger.warn('Revenue Hero widget did not load in time');
          onFinish();
        }}
      >
        <RHWidget form={form} rhData={rhData} close={onFinish} />
      </TimedRender>
    );
  }

  if (!showAccountCreateOnboardingModal) {
    return <IntroVideo firstName={firstName} onClose={onFinish} />;
  }

  return (
    <Row gutter={0}>
      <WelcomeNote flex="246px">
        <SpaceBetween>
          <PaddedContainer>
            <Space direction="vertical" size={32}>
              <Title>
                <FormattedMessage
                  id="intro_modal.title"
                  values={{
                    firstName,
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Title>
              <Description>
                <Space direction="vertical" size={24}>
                  <FormattedMessage id="intro_modal.message.1" />
                </Space>
              </Description>
            </Space>
          </PaddedContainer>
          <SvgContainer>
            <Astronaut />
          </SvgContainer>
        </SpaceBetween>
      </WelcomeNote>
      <Col flex="auto">
        <FormContainer>
          <Heading>
            <FormattedMessage id="intro_modal.form.heading" />
          </Heading>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label={<FormattedMessage id="intro_modal.form.role.label" />}
              name="role"
              rules={[
                {
                  required: true,
                  message: roleValidationText,
                },
              ]}
            >
              <Select
                closeMenuOnSelect
                isClearable
                isLoading={isRolesLoading}
                options={rolesOptions}
                placeholder={
                  userType === UserType.Partner ? (
                    <FormattedMessage id="settings.team_members.invite.placeholder.role_choose_only" />
                  ) : (
                    <FormattedMessage id="settings.team_members.invite.placeholder.role" />
                  )
                }
              />
            </Form.Item>
            <Form.Item
              name="team_size"
              rules={[
                {
                  required: true,
                  message: teamValidationText,
                },
              ]}
              label={<FormattedMessage id="intro_modal.form.team_size.label" />}
            >
              <Select
                options={TEAM_OPTIONS.map((option) => ({
                  label: option,
                  value: option,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="industry"
              rules={[
                {
                  required: true,
                  message: industryValidationText,
                },
              ]}
              label={<FormattedMessage id="intro_modal.form.industry.label" />}
            >
              <Select
                options={INDUSTRY_OPTIONS.map((option) => ({
                  label: translate(option.key as TranslationKey),
                  value: option.value,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="goals"
              rules={[
                {
                  required: true,
                  message: goalsValidationText,
                },
              ]}
              label={<FormattedMessage id="intro_modal.form.goals.label" />}
            >
              <Checkbox.Group>
                <Space direction="vertical">
                  {GOAL_OPTIONS.map((option) => (
                    <Checkbox value={option.value} key={option.key}>
                      {translate(option.key as TranslationKey)}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </Form.Item>
          </Form>
        </FormContainer>
        <Footer>
          <Button
            type="primary"
            iconPlacement="right"
            icon={<ArrowRight16 />}
            onClick={() => form.submit()}
            loading={isLoading}
            data-cy="intro_modal.form.button"
          >
            <FormattedMessage id="intro_modal.form.button" />
          </Button>
        </Footer>
      </Col>
    </Row>
  );
};

export const IntroModal: FC<React.PropsWithChildren<unknown>> = () => {
  const {
    user: { firstName, role, userType, emailId },
    userMeta = {} as UserMeta,
  } = useUser();
  const { metaData } = userMeta;
  const [visible, { setFalse: closeModal }] = useBoolean(
    !metaData?.profileSetupDone,
  );

  const [rhState, setRhState] = useState<RevenueHeroState | null>(null);

  if (!visible) {
    return null;
  }

  return (
    <StyledModal
      width={
        rhState === RevenueHeroState.Qualified
          ? IntroModalWidth.RevenueHeroWidget
          : IntroModalWidth.FormModal
      }
      open={visible}
      closable={false}
      footer={null}
      centered
      maskStyle={modalMaskCSS}
      zIndex={1012}
      destroyOnClose
    >
      <IntroModalContent
        firstName={firstName}
        emailId={emailId}
        userType={userType}
        originalRole={role}
        onFinish={closeModal}
        rhState={rhState}
        setRhState={setRhState}
      />
    </StyledModal>
  );
};
